<template>
  <b-sidebar
    id="add-variant-sidebar"
    :visible="show"
    bg-variant="white"
    sidebar-class="sidebar-lg"
    shadow
    backdrop
    no-header
    right
    @change="(visible) => $emit('update:show', visible)"
  >
    <template #default="{ hide }">
      <!-- Header -->
      <div class="d-flex justify-content-between align-items-center content-sidebar-header px-2 py-1">
        <h5 class="mb-0">
          {{ variantData ? 'Editar variante' : 'Agregar variante' }}
        </h5>

        <feather-icon
          class="ml-1 cursor-pointer"
          icon="XIcon"
          size="16"
          @click="hide"
        />
      </div>

      <validation-observer
        ref="variantRules"
      >
        <b-form class="p-2">
          <b-row>
            <b-col cols="12">
              <!-- TALLA - Campo principal que siempre se muestra -->
              <validation-provider
                v-slot="{ errors }"
                name="talla"
                rules="required"
              >
                <b-form-group
                  label="Talla:"
                  label-for="select-size"
                  class="mb-2"
                >
                  <v-select
                    id="select-size"
                    v-model="form.size_id"
                    :options="sizes"
                    :reduce="option => option.value"
                    label="display_name"
                    placeholder="Selecciona una talla"
                    :disabled="!!variantData"
                    :clearable="false"
                  />
                  <small
                    v-if="variantData"
                    class="text-muted"
                  >
                    La talla no puede modificarse una vez creada la variante
                  </small>
                  <small
                    v-else
                    class="text-danger"
                  >{{ errors[0] }}</small>
                </b-form-group>
              </validation-provider>

              <!-- SKU generado automáticamente -->
              <b-form-group
                label="SKU de la variante:"
                label-for="input-sku"
                class="mb-2"
              >
                <b-form-input
                  id="input-sku"
                  :value="variantData ? form.sku : generatedSku"
                  readonly
                  disabled
                />
                <small class="text-muted">
                  Generado automáticamente: Código-Color-Talla
                </small>
              </b-form-group>

              <!-- Presentación siempre fija -->
              <b-form-group
                label="Presentación:"
                label-for="input-presentation"
                class="mb-3"
              >
                <b-form-input
                  id="input-presentation"
                  value="Unidad"
                  readonly
                  disabled
                />
              </b-form-group>

              <!-- Big & Tall - Solo editable para variantes nuevas o en modo edición -->
              <b-form-group
                class="mb-2"
              >
                <b-form-checkbox
                  id="checkbox-big-tall"
                  v-model="form.is_big_tall"
                  name="is_big_tall"
                  :value="true"
                  :unchecked-value="false"
                >
                  Esta variante es de talla Big & Tall
                </b-form-checkbox>
              </b-form-group>

              <!-- Estado activo/inactivo - Solo en modo edición -->
              <b-form-group
                v-if="variantData"
                label="Estado:"
                label-for="checkbox-active"
                class="mb-2"
              >
                <b-form-checkbox
                  id="checkbox-active"
                  :checked="form.active === 'Y'"
                  name="active"
                  @change="form.active = $event ? 'Y' : 'N'"
                >
                  Variante activa
                </b-form-checkbox>
                <small class="text-muted">
                  Las variantes inactivas no pueden ser seleccionadas en pedidos o cotizaciones
                </small>
              </b-form-group>
            </b-col>
          </b-row>

          <div class="d-flex mt-2">
            <b-button
              v-ripple.400="'rgba(255, 255, 255, 0.15)'"
              variant="primary"
              class="mr-2"
              @click="submitForm"
            >
              {{ variantData ? 'Actualizar' : 'Guardar' }}
            </b-button>
            <b-button
              v-ripple.400="'rgba(186, 191, 199, 0.15)'"
              variant="outline-secondary"
              @click="hide"
            >
              Cancelar
            </b-button>
          </div>
        </b-form>
      </validation-observer>
    </template>
  </b-sidebar>
</template>

<script>
import axios from '@axios'
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import vSelect from 'vue-select'
import Ripple from 'vue-ripple-directive'

export default {
  name: 'ProductsVariantForm',

  components: {
    ValidationProvider,
    ValidationObserver,
    vSelect,
  },

  directives: {
    Ripple,
  },

  props: {
    show: {
      type: Boolean,
      required: true,
    },
    productId: {
      type: Number,
      required: true,
    },
    variantData: {
      type: Object,
      default: null,
    },
  },

  data() {
    return {
      form: {
        sku: '',
        presentation_id: null,
        size_id: null,
        is_big_tall: false,
        active: 'Y',
      },
      presentations: [],
      sizes: [],
      productData: null, // Para guardar información del producto actual
    }
  },

  computed: {
    generatedSku() {
      if (!this.productData || !this.form.size_id) {
        return 'Selecciona una talla para ver el SKU'
      }

      const colorName = this.productData.color ? this.productData.color.toUpperCase() : 'SIN-COLOR'
      const selectedSize = this.sizes.find(size => size.value === this.form.size_id)
      const sizeName = selectedSize ? selectedSize.name : ''

      return `${this.productData.code}-${colorName}-${sizeName}`
    },
  },

  watch: {
    show(newVal) {
      if (newVal) {
        this.loadData()
        if (this.variantData) {
          this.fillForm()
        } else {
          this.resetForm()
        }
      }
    },
  },

  methods: {
    async loadData() {
      try {
        // Cargar información del producto
        const productResponse = await axios.get(`product/${this.productId}`)
        this.productData = productResponse.data.data

        // Cargar presentaciones y establecer "Unidad" como predeterminada
        const presentationsResponse = await axios.get('presentation')
        this.presentations = presentationsResponse.data.data.map(item => ({
          value: item.id,
          name: item.name,
        }))

        // Buscar y establecer el ID de "Unidad"
        const unidadPresentation = this.presentations.find(p => p.name === 'Unidad')
        if (unidadPresentation) {
          this.form.presentation_id = unidadPresentation.value
        }

        // Cargar tallas
        const sizesResponse = await axios.get('size')
        this.sizes = sizesResponse.data.data.map(item => ({
          value: item.id,
          name: item.name,
          external_code: item.external_code,
          display_name: item.external_code ? `${item.name} (${item.external_code})` : item.name,
        }))
      } catch (error) {
        this.showErrors(error, 'cargar datos')
      }
    },

    fillForm() {
      // Buscar el ID de "Unidad" si no está ya establecido
      if (!this.form.presentation_id) {
        const unidadPresentation = this.presentations.find(p => p.name === 'Unidad')
        if (unidadPresentation) {
          this.form.presentation_id = unidadPresentation.value
        }
      }

      this.form = {
        sku: this.variantData.sku || '',
        presentation_id: this.form.presentation_id || this.variantData.presentation_id || null,
        size_id: this.variantData.size_id || null,
        is_big_tall: Boolean(this.variantData.is_big_tall),
        active: this.variantData.active === 'N' ? 'N' : 'Y',
      }
    },

    resetForm() {
      // Buscar y establecer el ID de "Unidad"
      const unidadPresentation = this.presentations.find(p => p.name === 'Unidad')
      const unidadId = unidadPresentation ? unidadPresentation.value : null

      this.form = {
        sku: '',
        presentation_id: unidadId,
        size_id: null,
        is_big_tall: false,
        active: 'Y',
      }
    },

    async submitForm() {
      const isValid = await this.$refs.variantRules.validate()
      if (!isValid) return

      try {
        if (this.variantData && this.variantData.id) {
          // Actualizar variante existente - solo campos permitidos
          const updateData = {
            active: this.form.active,
            is_big_tall: Boolean(this.form.is_big_tall),
          }

          await axios.put(`product-variant/${this.variantData.id}`, updateData)
          this.makeToast('success', 'Variante actualizada', 'La variante ha sido actualizada exitosamente.')
        } else {
          // Crear nueva variante - el SKU se genera automáticamente en el backend
          const variantData = {
            product_id: this.productId,
            size_id: this.form.size_id,
            is_big_tall: Boolean(this.form.is_big_tall),
          }

          await axios.post('product-variant', variantData)
          this.makeToast('success', 'Variante agregada', 'La variante ha sido agregada exitosamente.')
        }

        this.$emit('refresh-data')
        this.$emit('update:show', false)
      } catch (error) {
        // Personalizar mensaje de error según la acción
        const action = this.variantData ? 'actualizar' : 'crear'
        this.showErrors(error, action)
      }
    },

    makeToast(variant = null, title = null, message = null) {
      this.$bvToast.toast(message, {
        title,
        variant,
        solid: true,
      })
    },

    showErrors(error, action = null) {
      let errorMessage = action ? `Error al ${action} la variante` : 'Ha ocurrido un error inesperado'
      let errorTitle = 'Error'

      if (error.response?.data) {
        const { data } = error.response

        // Formato del BaseController: {res: false, msg: "", error: "", data: ""}
        if (data.msg) {
          errorMessage = data.msg
          if (data.data) {
            errorMessage = data.data // El mensaje específico está en data

            // Mejorar mensaje para SKU duplicado
            if (data.data.includes('Ya existe una variante con este SKU')) {
              errorMessage = 'Ya existe una variante con esa combinación de talla para este producto. Por favor, selecciona una talla diferente.'
            }
          }
        } else if (data.message) {
          // Formato Laravel estándar: {message: ""}
          errorMessage = data.message
        } else if (data.errors) {
          // Errores de validación Laravel: {errors: {}}
          const [firstError] = Object.values(data.errors)
          if (Array.isArray(firstError) && firstError.length > 0) {
            [errorMessage] = firstError
          }
        }

        // Si hay un código de estado específico, personalizar el título
        if (error.response.status === 422) {
          errorTitle = 'Error de validación'
        } else if (error.response.status === 409) {
          errorTitle = 'Conflicto'
        }
      }

      this.$bvToast.toast(errorMessage, {
        title: errorTitle,
        variant: 'danger',
        solid: true,
        autoHideDelay: 5000, // Mostrar por 5 segundos
      })
    },
  },
}
</script>

<style lang="scss" scoped>
.content-sidebar-header {
  border-bottom: 1px solid #ebe9f1;
}
</style>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';
</style>
